// frontend/app/components/lessonPlans/blocks/LessonCheckBlocks.js
//
// Answerable blocks. Each writes through `onAnswer(block, value)`, which grades
// against the block's authored key and returns a NEW session state — these
// components never mutate what they are handed.

import React, { useCallback, useMemo } from "react";
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import {
  ActionButton,
  AnswerFeedback,
  BlockCard,
  BlockPrompt,
  Pill,
} from "./LessonBlockShell";
import VoiceAnswerControl from "./VoiceAnswerControl";

const optionLetter = (index) => String.fromCharCode(65 + index);

const ConceptTag = ({ block, colors, conceptLabels, t }) => {
  const label = conceptLabels?.[block.concept_key];
  if (!label) return null;
  return (
    <View style={styles.conceptRow}>
      <Pill
        colors={colors}
        label={label}
        icon="tag-outline"
        accent={colors.textMuted}
      />
    </View>
  );
};

const OptionRow = ({
  colors,
  label,
  index,
  selected,
  disabled,
  multi,
  onPress,
}) => (
  <TouchableOpacity
    onPress={onPress}
    disabled={disabled}
    accessibilityRole={multi ? "checkbox" : "radio"}
    accessibilityState={{ checked: selected, disabled }}
    accessibilityLabel={label}
    style={[
      styles.option,
      {
        backgroundColor: selected ? colors.accentGlow : colors.subtleBg,
        borderColor: selected ? colors.accentPrimary : colors.border,
        opacity: disabled && !selected ? 0.65 : 1,
      },
    ]}
  >
    <View
      style={[
        styles.optionMarker,
        {
          borderColor: selected ? colors.accentPrimary : colors.borderStrong,
          backgroundColor: selected ? colors.accentPrimary : "transparent",
          borderRadius: multi ? 5 : 12,
        },
      ]}
    >
      {selected ? (
        <MaterialCommunityIcons name="check" size={13} color="#ffffff" />
      ) : (
        <Text style={[styles.optionLetter, { color: colors.textMuted }]}>
          {optionLetter(index)}
        </Text>
      )}
    </View>
    <Text style={[styles.optionText, { color: colors.textPrimary }]}>{label}</Text>
  </TouchableOpacity>
);

export const MultipleChoiceBlock = ({
  block,
  colors,
  entry,
  onAnswer,
  readOnly,
  conceptLabels,
  t,
}) => {
  const options = Array.isArray(block.options) ? block.options : [];
  const answered = entry?.value !== undefined && entry?.value !== null;

  return (
    <BlockCard colors={colors}>
      <BlockPrompt colors={colors}>{block.prompt}</BlockPrompt>
      {options.map((option, index) => (
        <OptionRow
          key={`${block.id}-${option.id}`}
          colors={colors}
          index={index}
          label={option.text}
          selected={entry?.value === option.id}
          disabled={readOnly}
          onPress={() => onAnswer(block, option.id)}
        />
      ))}
      {answered ? (
        <AnswerFeedback
          colors={colors}
          isCorrect={entry.isCorrect}
          explanation={block.explanation}
          t={t}
        />
      ) : null}
      <ConceptTag block={block} colors={colors} conceptLabels={conceptLabels} t={t} />
    </BlockCard>
  );
};

export const MultiSelectBlock = ({
  block,
  colors,
  entry,
  onAnswer,
  onCheck,
  readOnly,
  conceptLabels,
  t,
}) => {
  const translate = t || ((value) => value);
  const options = Array.isArray(block.options) ? block.options : [];
  const selected = useMemo(
    () => (Array.isArray(entry?.value) ? entry.value : []),
    [entry]
  );
  // Every toggle re-grades so nothing is lost, but a half-built selection is
  // not a wrong answer — feedback waits for an explicit check.
  const checked = entry?.checked === true;

  const toggle = useCallback(
    (optionId) => {
      const next = selected.includes(optionId)
        ? selected.filter((id) => id !== optionId)
        : [...selected, optionId];
      onAnswer(block, next);
    },
    [block, onAnswer, selected]
  );

  return (
    <BlockCard colors={colors}>
      <BlockPrompt colors={colors}>{block.prompt}</BlockPrompt>
      <Text style={[styles.hint, { color: colors.textMuted }]}>
        {translate("Select all that apply.")}
      </Text>
      {options.map((option, index) => (
        <OptionRow
          key={`${block.id}-${option.id}`}
          colors={colors}
          index={index}
          multi
          label={option.text}
          selected={selected.includes(option.id)}
          disabled={readOnly}
          onPress={() => toggle(option.id)}
        />
      ))}
      {checked ? (
        <AnswerFeedback
          colors={colors}
          isCorrect={entry.isCorrect}
          explanation={block.explanation}
          t={t}
        />
      ) : (
        <View style={styles.checkRow}>
          <ActionButton
            colors={colors}
            label={translate("Check answer")}
            icon="check"
            variant="outline"
            disabled={readOnly || selected.length === 0}
            onPress={() => onCheck && onCheck(block)}
          />
        </View>
      )}
      <ConceptTag block={block} colors={colors} conceptLabels={conceptLabels} t={t} />
    </BlockCard>
  );
};

/**
 * "Trace the blood" — tap items in order. Tapping a chosen item removes it and
 * everything after it, so a mistake is one tap to undo rather than a reset.
 */
export const OrderingBlock = ({
  block,
  colors,
  entry,
  onAnswer,
  readOnly,
  conceptLabels,
  t,
}) => {
  const translate = t || ((value) => value);
  const items = Array.isArray(block.items) ? block.items : [];
  const chosen = useMemo(
    () => (Array.isArray(entry?.value) ? entry.value : []),
    [entry]
  );
  const remaining = items.filter((item) => !chosen.includes(item.id));
  const complete = chosen.length === items.length && items.length > 0;

  const labelFor = useCallback(
    (id) => items.find((item) => item.id === id)?.text || id,
    [items]
  );

  const choose = useCallback(
    (itemId) => onAnswer(block, [...chosen, itemId]),
    [block, chosen, onAnswer]
  );

  const undoTo = useCallback(
    (index) => onAnswer(block, chosen.slice(0, index)),
    [block, chosen, onAnswer]
  );

  return (
    <BlockCard colors={colors}>
      <BlockPrompt colors={colors}>{block.prompt}</BlockPrompt>

      <View style={[styles.sequence, { borderColor: colors.border, backgroundColor: colors.subtleBg }]}>
        {chosen.length === 0 ? (
          <Text style={[styles.hint, { color: colors.textMuted }]}>
            {translate("Tap the steps below in order.")}
          </Text>
        ) : (
          chosen.map((id, index) => (
            <TouchableOpacity
              key={`${block.id}-chosen-${id}`}
              onPress={() => !readOnly && undoTo(index)}
              disabled={readOnly}
              accessibilityRole="button"
              accessibilityLabel={`${index + 1}. ${labelFor(id)}`}
              style={[
                styles.sequenceItem,
                { backgroundColor: colors.panelBg, borderColor: colors.accentPrimary },
              ]}
            >
              <Text style={[styles.sequenceIndex, { color: colors.accentPrimary }]}>
                {index + 1}
              </Text>
              <Text style={[styles.sequenceText, { color: colors.textPrimary }]}>
                {labelFor(id)}
              </Text>
              {!readOnly ? (
                <MaterialCommunityIcons
                  name="close"
                  size={14}
                  color={colors.textMuted}
                />
              ) : null}
            </TouchableOpacity>
          ))
        )}
      </View>

      {remaining.length > 0 ? (
        <View style={styles.choiceRow}>
          {remaining.map((item) => (
            <TouchableOpacity
              key={`${block.id}-pick-${item.id}`}
              onPress={() => choose(item.id)}
              disabled={readOnly}
              accessibilityRole="button"
              accessibilityLabel={item.text}
              style={[
                styles.choiceChip,
                { backgroundColor: colors.subtleBg, borderColor: colors.borderStrong },
              ]}
            >
              <Text style={[styles.choiceText, { color: colors.textPrimary }]}>
                {item.text}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      ) : null}

      {complete ? (
        <AnswerFeedback
          colors={colors}
          isCorrect={entry.isCorrect}
          explanation={block.explanation}
          t={t}
        />
      ) : null}
      <ConceptTag block={block} colors={colors} conceptLabels={conceptLabels} t={t} />
    </BlockCard>
  );
};

/** Free text: `short_answer` and `prediction` share this renderer. */
export const TextResponseBlock = ({
  block,
  colors,
  entry,
  onAnswer,
  readOnly,
  conceptLabels,
  // Speaking is offered wherever a microphone exists. Both optional: without
  // them the block is typed, exactly as it was.
  language,
  onExplorationEvent,
  t,
}) => {
  const translate = t || ((value) => value);
  const value = typeof entry?.value === "string" ? entry.value : "";
  const isPrediction = block.type === "prediction";

  return (
    <BlockCard colors={colors} accent={isPrediction ? colors.warning : undefined}>
      {isPrediction ? (
        <Text style={[styles.eyebrow, { color: colors.warning }]}>
          {translate("Predict first")}
        </Text>
      ) : null}
      <BlockPrompt colors={colors}>{block.prompt}</BlockPrompt>
      <VoiceAnswerControl
        colors={colors}
        accent={isPrediction ? colors.warning : colors.accentPrimary}
        value={value}
        onChange={(next) => onAnswer(block, next)}
        readOnly={readOnly}
        language={language}
        onEvent={onExplorationEvent}
        t={t}
      />
      <TextInput
        value={value}
        onChangeText={(next) => onAnswer(block, next)}
        editable={!readOnly}
        multiline
        placeholder={block.placeholder || translate("Type your answer…")}
        placeholderTextColor={colors.textMuted}
        accessibilityLabel={block.prompt}
        style={[
          styles.input,
          {
            backgroundColor: colors.subtleBg,
            borderColor: colors.border,
            color: colors.textPrimary,
          },
        ]}
      />
      <ConceptTag block={block} colors={colors} conceptLabels={conceptLabels} t={t} />
    </BlockCard>
  );
};

const styles = StyleSheet.create({
  hint: { fontSize: 12, marginBottom: 8 },
  eyebrow: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.7,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 11,
    marginBottom: 8,
  },
  optionMarker: {
    width: 24,
    height: 24,
    borderWidth: 1.5,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  optionLetter: { fontSize: 11, fontWeight: "700" },
  optionText: { fontSize: 14, lineHeight: 20, flex: 1 },

  sequence: {
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: "dashed",
    padding: 10,
    minHeight: 52,
    marginBottom: 10,
  },
  sequenceItem: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 6,
    gap: 8,
  },
  sequenceIndex: { fontSize: 12, fontWeight: "800", marginRight: 8 },
  sequenceText: { fontSize: 13, flex: 1 },

  choiceRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  choiceChip: {
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  choiceText: { fontSize: 13, fontWeight: "600" },

  input: {
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    lineHeight: 20,
    minHeight: 76,
    textAlignVertical: "top",
  },
  checkRow: { flexDirection: "row", marginTop: 4 },
  conceptRow: { flexDirection: "row", marginTop: 10 },
});
